import { useEffect, useState, useMemo } from "react";
import {
  Form,
  Link,
  useLoaderData,
  useActionData,
  useNavigation,
  useFetcher,
} from "react-router-dom";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { Button, LinkButton, MarkdownEditor } from "../../components/common";
import { generateSlug } from "../../utils";
import type { CreateEpisodeLoaderData, EpisodeEditorData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import type { EpisodeStatus, EpisodeType } from "../../types/api";
import AudioProcessing from "../../components/podcasts/AudioProcessing";
import AudioUploader from "../../components/common/AudioUploader";
import TranscriptViewer from "../../components/transcripts/TranscriptViewer";
import styles from "./EpisodeEditorPage.module.css";

const EPISODE_TYPES: { value: EpisodeType; label: string; description: string }[] = [
  { value: "full", label: "Full Episode", description: "A regular episode" },
  { value: "trailer", label: "Trailer", description: "A preview or promotional episode" },
  { value: "bonus", label: "Bonus", description: "Extra content for your audience" },
];

const STATUS_OPTIONS: { value: EpisodeStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "scheduled", label: "Scheduled" },
  { value: "published", label: "Published" },
];

interface EpisodeEditorPageProps {
  mode: "create" | "edit";
}

export default function EpisodeEditorPage({ mode }: EpisodeEditorPageProps) {
  const loaderData = useLoaderData() as CreateEpisodeLoaderData | EpisodeEditorData;
  const actionData = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const deleteFetcher = useFetcher();

  const { podcast, organizationId } = loaderData;
  const episode = "episode" in loaderData ? loaderData.episode : null;

  const isSubmitting = navigation.state === "submitting";
  const isDeleting = deleteFetcher.state === "submitting";

  const [title, setTitle] = useState(episode?.title || "");
  const [slugOverride, setSlugOverride] = useState(episode?.slug || "");
  const [showSlugInput, setShowSlugInput] = useState(!!episode?.slug);
  const [status, setStatus] = useState<EpisodeStatus>(episode?.status || "draft");
  const [showNotes, setShowNotes] = useState(episode?.showNotes || "");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [audioUrl, setAudioUrl] = useState(episode?.audioUrl || "");
  const [audioS3Key, setAudioS3Key] = useState("");
  const [uploadMode, setUploadMode] = useState<"upload" | "url">(
    episode?.audioUrl ? "url" : "upload",
  );

  // Detect user's timezone
  const userTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "UTC";
    }
  }, []);

  const generatedSlug = useMemo(() => generateSlug(title), [title]);
  const displaySlug = slugOverride || generatedSlug;

  useEffect(() => {
    if (actionData?.success === true) {
      toast.success(mode === "create" ? "Episode created!" : "Episode saved!");
    } else if (actionData?.error) {
      toast.error(actionData.error);
    }
  }, [actionData, mode]);

  useEffect(() => {
    if (deleteFetcher.data?.error) {
      toast.error(deleteFetcher.data.error);
      setShowDeleteModal(false);
    }
  }, [deleteFetcher.data]);

  const handleDelete = () => {
    if (episode) {
      deleteFetcher.submit(null, {
        method: "POST",
        action: `/organizations/${organizationId}/podcasts/${podcast.id}/episodes/${episode.id}/delete`,
      });
    }
  };

  // Format datetime for input
  const formatDateTimeForInput = (dateString: string | null | undefined): string => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toISOString().slice(0, 16);
    } catch {
      return "";
    }
  };

  return (
    <>
      <Header />
      <div className="container">
        <div className={styles.editorContainer}>
          <div className={styles.backSection}>
            <Link
              to={`/organizations/${organizationId}/podcasts/${podcast.id}`}
              className={styles.backLink}
            >
              &larr; Back to {podcast.name}
            </Link>
          </div>

          <div className={styles.card}>
            <h1>{mode === "create" ? "Create Episode" : "Edit Episode"}</h1>
            <p className={styles.description}>
              {mode === "create"
                ? "Add a new episode to your podcast."
                : "Update your episode details."}
            </p>

            <Form method="post" className={styles.form}>
              {/* Episode Title */}
              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.title ? styles.error : ""}`}
              >
                <label htmlFor="title">Episode Title *</label>
                <input
                  id="title"
                  name="title"
                  type="text"
                  placeholder="My Amazing Episode"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
                {actionData?.fieldErrors?.title && (
                  <span className={styles.formError}>{actionData.fieldErrors.title}</span>
                )}
              </div>

              {/* Slug */}
              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.slug ? styles.error : ""}`}
              >
                <label htmlFor="slug">
                  URL Slug
                  {!showSlugInput && displaySlug && (
                    <button
                      type="button"
                      className={styles.editSlugButton}
                      onClick={() => setShowSlugInput(true)}
                    >
                      Edit
                    </button>
                  )}
                </label>
                {showSlugInput ? (
                  <input
                    id="slug"
                    name="slug"
                    type="text"
                    placeholder="my-amazing-episode"
                    value={slugOverride}
                    onChange={(e) => setSlugOverride(e.target.value.toLowerCase())}
                    disabled={isSubmitting}
                  />
                ) : (
                  <>
                    <div className={styles.slugPreview}>
                      {displaySlug || (
                        <span className={styles.slugPlaceholder}>auto-generated-from-title</span>
                      )}
                    </div>
                    <input type="hidden" name="slug" value={slugOverride} />
                  </>
                )}
                {actionData?.fieldErrors?.slug && (
                  <span className={styles.formError}>{actionData.fieldErrors.slug}</span>
                )}
              </div>

              {/* Description */}
              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.description ? styles.error : ""}`}
              >
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  placeholder="A brief description of this episode..."
                  rows={3}
                  defaultValue={episode?.description || ""}
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.description && (
                  <span className={styles.formError}>{actionData.fieldErrors.description}</span>
                )}
                <span className={styles.hint}>
                  Appears in podcast apps. Keep it concise (1-2 sentences).
                </span>
              </div>

              {/* Show Notes */}
              <div className={styles.formGroup}>
                <label htmlFor="showNotes">Show Notes</label>
                <MarkdownEditor
                  id="showNotes"
                  name="showNotes"
                  value={showNotes}
                  onChange={setShowNotes}
                  placeholder="Detailed notes, links, timestamps..."
                  disabled={isSubmitting}
                  minHeight={250}
                  maxLength={50000}
                  hint="Include timestamps, links, and resources mentioned in the episode."
                  error={actionData?.fieldErrors?.showNotes}
                  enablePreview={true}
                  initialViewMode="write"
                />
              </div>

              {/* Audio Section */}
              <div className={styles.formSection}>
                <h3>Audio</h3>
                <div className={styles.audioSection}>
                  <div className={styles.formGroup}>
                    <span className={styles.formLabel}>Audio</span>
                    <div className={styles.audioToggle}>
                      <button
                        type="button"
                        className={`${styles.toggleOption} ${uploadMode === "upload" ? styles.toggleActive : ""}`}
                        onClick={() => setUploadMode("upload")}
                      >
                        Upload file
                      </button>
                      <button
                        type="button"
                        className={`${styles.toggleOption} ${uploadMode === "url" ? styles.toggleActive : ""}`}
                        onClick={() => setUploadMode("url")}
                      >
                        Paste URL
                      </button>
                    </div>

                    {uploadMode === "upload" ? (
                      <>
                        <AudioUploader
                          orgId={organizationId}
                          podcastId={podcast.id}
                          onComplete={(s3Key) => {
                            setAudioS3Key(s3Key);
                          }}
                          onRemove={() => setAudioS3Key("")}
                          disabled={isSubmitting}
                        />
                        <input type="hidden" name="audioS3Key" value={audioS3Key} />
                      </>
                    ) : (
                      <>
                        <input
                          id="audioUrl"
                          name="audioUrl"
                          type="url"
                          placeholder="https://example.com/episode.mp3"
                          value={audioUrl}
                          onChange={(e) => setAudioUrl(e.target.value)}
                          disabled={isSubmitting}
                        />
                        <span className={styles.hint}>
                          Direct link to your audio file (MP3, M4A, etc.)
                        </span>
                      </>
                    )}
                    {actionData?.fieldErrors?.audioUrl && (
                      <span className={styles.formError}>{actionData.fieldErrors.audioUrl}</span>
                    )}
                  </div>

                  <div className={styles.twoColumn}>
                    <div
                      className={`${styles.formGroup} ${actionData?.fieldErrors?.audioDurationSeconds ? styles.error : ""}`}
                    >
                      <label htmlFor="audioDurationSeconds">Duration (seconds)</label>
                      <input
                        id="audioDurationSeconds"
                        name="audioDurationSeconds"
                        type="number"
                        min="0"
                        placeholder="3600"
                        defaultValue={episode?.audioDurationSeconds || ""}
                        disabled={isSubmitting}
                      />
                      {actionData?.fieldErrors?.audioDurationSeconds && (
                        <span className={styles.formError}>
                          {actionData.fieldErrors.audioDurationSeconds}
                        </span>
                      )}
                    </div>

                    <div
                      className={`${styles.formGroup} ${actionData?.fieldErrors?.audioFileSizeBytes ? styles.error : ""}`}
                    >
                      <label htmlFor="audioFileSizeBytes">File Size (bytes)</label>
                      <input
                        id="audioFileSizeBytes"
                        name="audioFileSizeBytes"
                        type="number"
                        min="0"
                        placeholder="50000000"
                        defaultValue={episode?.audioFileSizeBytes || ""}
                        disabled={isSubmitting}
                      />
                      {actionData?.fieldErrors?.audioFileSizeBytes && (
                        <span className={styles.formError}>
                          {actionData.fieldErrors.audioFileSizeBytes}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Episode Details */}
              <div className={styles.formSection}>
                <h3>Episode Details</h3>
                <div className={styles.threeColumn}>
                  <div
                    className={`${styles.formGroup} ${actionData?.fieldErrors?.episodeType ? styles.error : ""}`}
                  >
                    <label htmlFor="episodeType">Episode Type</label>
                    <select
                      id="episodeType"
                      name="episodeType"
                      defaultValue={episode?.episodeType || "full"}
                      disabled={isSubmitting}
                    >
                      {EPISODE_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {actionData?.fieldErrors?.episodeType && (
                      <span className={styles.formError}>{actionData.fieldErrors.episodeType}</span>
                    )}
                  </div>

                  <div
                    className={`${styles.formGroup} ${actionData?.fieldErrors?.seasonNumber ? styles.error : ""}`}
                  >
                    <label htmlFor="seasonNumber">Season</label>
                    <input
                      id="seasonNumber"
                      name="seasonNumber"
                      type="number"
                      min="1"
                      max="9999"
                      placeholder="1"
                      defaultValue={episode?.seasonNumber || ""}
                      disabled={isSubmitting}
                    />
                    {actionData?.fieldErrors?.seasonNumber && (
                      <span className={styles.formError}>
                        {actionData.fieldErrors.seasonNumber}
                      </span>
                    )}
                  </div>

                  <div
                    className={`${styles.formGroup} ${actionData?.fieldErrors?.episodeNumber ? styles.error : ""}`}
                  >
                    <label htmlFor="episodeNumber">Episode #</label>
                    <input
                      id="episodeNumber"
                      name="episodeNumber"
                      type="number"
                      min="1"
                      max="99999"
                      placeholder="1"
                      defaultValue={episode?.episodeNumber || ""}
                      disabled={isSubmitting}
                    />
                    {actionData?.fieldErrors?.episodeNumber && (
                      <span className={styles.formError}>
                        {actionData.fieldErrors.episodeNumber}
                      </span>
                    )}
                  </div>
                </div>

                <div className={styles.twoColumn} style={{ marginTop: "var(--spacing-4)" }}>
                  <div className={styles.formGroup}>
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        name="explicit"
                        value="true"
                        defaultChecked={episode?.explicit || false}
                        disabled={isSubmitting}
                      />
                      <span>Contains explicit content</span>
                    </label>
                    <span className={styles.hint}>
                      Mark if this episode contains adult language or themes
                    </span>
                  </div>

                  {podcast.isPremium && (
                    <div className={styles.formGroup}>
                      <label className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          name="isPremium"
                          value="true"
                          defaultChecked={episode?.isPremium || false}
                          disabled={isSubmitting}
                        />
                        <span>Premium episode</span>
                      </label>
                      <span className={styles.hint}>Only available to paid subscribers</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Publishing */}
              <div className={styles.formSection}>
                <h3>Publishing</h3>
                <div
                  className={`${styles.formGroup} ${actionData?.fieldErrors?.status ? styles.error : ""}`}
                >
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as EpisodeStatus)}
                    disabled={isSubmitting}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {actionData?.fieldErrors?.status && (
                    <span className={styles.formError}>{actionData.fieldErrors.status}</span>
                  )}
                </div>

                <div
                  className={`${styles.schedulingSection} ${status === "scheduled" ? styles.visible : ""}`}
                >
                  <div
                    className={`${styles.formGroup} ${actionData?.fieldErrors?.scheduledAt ? styles.error : ""}`}
                  >
                    <label htmlFor="scheduledAt">Scheduled Publish Date</label>
                    <input
                      id="scheduledAt"
                      name="scheduledAt"
                      type="datetime-local"
                      defaultValue={formatDateTimeForInput(episode?.scheduledAt)}
                      disabled={isSubmitting}
                    />
                    {actionData?.fieldErrors?.scheduledAt && (
                      <span className={styles.formError}>{actionData.fieldErrors.scheduledAt}</span>
                    )}
                  </div>
                  <div className={styles.timezoneDisplay}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    <span>Times shown in {userTimezone}</span>
                  </div>
                </div>
              </div>

              {/* Optional: Transcript URL */}
              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.transcriptUrl ? styles.error : ""}`}
              >
                <label htmlFor="transcriptUrl">Transcript URL (optional)</label>
                <input
                  id="transcriptUrl"
                  name="transcriptUrl"
                  type="url"
                  placeholder="https://example.com/transcript.txt"
                  defaultValue={episode?.transcriptUrl || ""}
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.transcriptUrl && (
                  <span className={styles.formError}>{actionData.fieldErrors.transcriptUrl}</span>
                )}
                <span className={styles.hint}>
                  Link to a transcript file (SRT, VTT, or plain text)
                </span>
              </div>

              {/* Form Actions */}
              <div className={styles.formActions}>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  loadingText="Saving..."
                >
                  {mode === "create" ? "Create Episode" : "Save Changes"}
                </Button>
                <LinkButton
                  to={`/organizations/${organizationId}/podcasts/${podcast.id}`}
                  variant="secondary"
                >
                  Cancel
                </LinkButton>
              </div>
            </Form>

            {mode === "edit" && episode?.audioUrl && (
              <AudioProcessing
                organizationId={organizationId}
                podcastId={podcast.id}
                episodeId={episode.id}
              />
            )}

            {mode === "edit" && episode && (
              <TranscriptViewer
                organizationId={organizationId}
                podcastId={podcast.id}
                episodeId={episode.id}
              />
            )}

            {/* Danger Zone - only in edit mode */}
            {mode === "edit" && episode && (
              <div className={styles.dangerZone} style={{ marginTop: "var(--spacing-8)" }}>
                <h3>Danger Zone</h3>
                <p>
                  Deleting this episode is permanent and cannot be undone. All associated data
                  including analytics will be lost.
                </p>
                <button
                  type="button"
                  className={styles.deleteButton}
                  onClick={() => setShowDeleteModal(true)}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete Episode"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className={styles.modal}
          onClick={() => setShowDeleteModal(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setShowDeleteModal(false);
          }}
          role="presentation"
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            <h3>Delete Episode</h3>
            <p>Are you sure you want to delete "{episode?.title}"? This action cannot be undone.</p>
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.deleteButton}
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete Episode"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
